import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Plus, Edit2, Trash2, X, Check, ChevronLeft, AlertTriangle } from 'lucide-react';
import {
  adminFetchTopicsThunk, adminCreateTopicThunk, adminUpdateTopicThunk, adminDeleteTopicThunk,
  adminFetchQuestionsThunk, adminCreateQuestionThunk, adminUpdateQuestionThunk, adminDeleteQuestionThunk,
} from '../../../redux/credentialQuiz/credentialQuizActionThunks';
import {
  selectAdminQuizTopics, selectAdminQuestionsForTopic, selectAdminQuizLoading, selectAdminQuizError,
} from '../../../redux/credentialQuiz/credentialQuizSlice';

const LEVELS = ['beginner', 'intermediate', 'advanced'];
const EMPTY_TOPIC_FORM = { key: '', name: '', description: '', group_label: '', sort_order: 0, is_active: true };
const EMPTY_QUESTION_FORM = {
  level: 'beginner', question_text: '', choices: [{ key: 'a', text: '' }, { key: 'b', text: '' }],
  correct_choice_key: 'a', explanation: '', sort_order: 0, is_active: true,
};

// Full admin CRUD pipeline for the standalone Knowledge Assessment system --
// create/edit/delete both topics (sections) and their questions, entirely
// through this UI, not a seed-script-only feature. Mirrors
// AdminNewsModerationComponent.jsx's theme/fetch/error-banner conventions.
const AdminCredentialQuizComponent = ({ darkMode = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const topics = useSelector(selectAdminQuizTopics);
  const loading = useSelector(selectAdminQuizLoading);
  const error = useSelector(selectAdminQuizError);

  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [topicModal, setTopicModal] = useState(null); // 'create' | topicObj | null
  const [topicForm, setTopicForm] = useState(EMPTY_TOPIC_FORM);
  const [questionModal, setQuestionModal] = useState(null); // 'create' | questionObj | null
  const [questionForm, setQuestionForm] = useState(EMPTY_QUESTION_FORM);

  const questions = useSelector((s) => selectAdminQuestionsForTopic(s, selectedTopicId));
  const selectedTopic = topics.find((t) => t.id === selectedTopicId);

  const themeClasses = {
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    input: darkMode ? 'bg-gray-900 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900',
    rowHover: darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50',
  };

  useEffect(() => {
    if (topics.length === 0) dispatch(adminFetchTopicsThunk());
  }, [dispatch, topics.length]);

  useEffect(() => {
    if (selectedTopicId) dispatch(adminFetchQuestionsThunk(selectedTopicId));
  }, [dispatch, selectedTopicId]);

  const openTopicModal = (topic) => {
    setTopicForm(topic ? { ...topic } : EMPTY_TOPIC_FORM);
    setTopicModal(topic || 'create');
  };

  const saveTopic = async () => {
    // Same fix as saveQuestion below: a bare `await dispatch(thunk())` always
    // resolves regardless of success/failure, so the modal was closing even
    // on a real save failure. .unwrap() + catch keeps it open so adminError
    // is actually visible when that happens.
    try {
      if (topicModal === 'create') {
        await dispatch(adminCreateTopicThunk(topicForm)).unwrap();
      } else {
        await dispatch(adminUpdateTopicThunk({ topicId: topicModal.id, payload: topicForm })).unwrap();
      }
      setTopicModal(null);
    } catch {
      // adminError is already set by the thunk's own .rejected case.
    }
  };

  const removeTopic = async (topic) => {
    if (!window.confirm(t('admin.credentialQuiz.deleteTopicConfirm', { name: topic.name, count: topic.question_count }))) return;
    await dispatch(adminDeleteTopicThunk(topic.id));
    if (selectedTopicId === topic.id) setSelectedTopicId(null);
  };

  const openQuestionModal = (question) => {
    setQuestionForm(question ? { ...question, choices: question.choices } : { ...EMPTY_QUESTION_FORM });
    setQuestionModal(question || 'create');
  };

  const updateChoice = (idx, field, value) => {
    setQuestionForm((f) => {
      const choices = [...f.choices];
      choices[idx] = { ...choices[idx], [field]: value };
      return { ...f, choices };
    });
  };

  const addChoice = () => {
    setQuestionForm((f) => ({ ...f, choices: [...f.choices, { key: String.fromCharCode(97 + f.choices.length), text: '' }] }));
  };

  const removeChoice = (idx) => {
    setQuestionForm((f) => ({ ...f, choices: f.choices.filter((_, i) => i !== idx) }));
  };

  const saveQuestion = async () => {
    // Was a bare `await dispatch(thunk())` -- that promise always resolves
    // (fulfilled or rejected), so the modal closed as if it succeeded even
    // on a real failure. .unwrap() + catch keeps it open so the now-wired
    // adminError banner above is actually visible when that happens.
    try {
      if (questionModal === 'create') {
        await dispatch(adminCreateQuestionThunk({ topicId: selectedTopicId, payload: questionForm })).unwrap();
      } else {
        await dispatch(adminUpdateQuestionThunk({ questionId: questionModal.id, payload: questionForm })).unwrap();
      }
      setQuestionModal(null);
    } catch {
      // adminError is already set by the thunk's own .rejected case.
    }
  };

  const removeQuestion = async (question) => {
    if (!window.confirm(t('admin.credentialQuiz.deleteQuestionConfirm'))) return;
    await dispatch(adminDeleteQuestionThunk({ questionId: question.id, topicId: selectedTopicId }));
  };

  if (error) {
    // eslint-disable-next-line no-console
    console.error('Credential Quiz admin error:', error);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className={`text-xl font-bold flex items-center gap-2 ${themeClasses.text}`}>
          <GraduationCap className="w-5 h-5 text-blue-400" />
          {t('admin.credentialQuiz.heading')}
        </h2>
        {!selectedTopicId && (
          <button
            onClick={() => openTopicModal(null)}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
          >
            <Plus size={16} /> {t('admin.credentialQuiz.newTopic')}
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
          <AlertTriangle size={16} /> {String(error)}
        </div>
      )}

      {!selectedTopicId ? (
        <div className={`rounded-xl border ${themeClasses.border} overflow-hidden`}>
          <table className="w-full text-sm">
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr className={themeClasses.textSecondary}>
                <th className="text-left px-4 py-2.5 font-medium">{t('admin.credentialQuiz.table.topic')}</th>
                <th className="text-left px-4 py-2.5 font-medium">{t('admin.credentialQuiz.table.group')}</th>
                <th className="text-left px-4 py-2.5 font-medium">{t('admin.credentialQuiz.table.questions')}</th>
                <th className="text-left px-4 py-2.5 font-medium">{t('admin.credentialQuiz.table.active')}</th>
                <th className="text-right px-4 py-2.5 font-medium">{t('admin.credentialQuiz.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && topics.length === 0 && (
                <tr><td colSpan={5} className={`px-4 py-6 text-center ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.loading')}</td></tr>
              )}
              {topics.map((topic) => (
                <tr key={topic.id} className={`border-t ${themeClasses.border} ${themeClasses.rowHover}`}>
                  <td className={`px-4 py-2.5 ${themeClasses.text}`}>
                    <button className="hover:underline text-left" onClick={() => setSelectedTopicId(topic.id)}>
                      {topic.name}
                    </button>
                    <div className={`text-xs ${themeClasses.textMuted}`}>{topic.key}</div>
                  </td>
                  <td className={`px-4 py-2.5 ${themeClasses.textSecondary}`}>{topic.group_label || '—'}</td>
                  <td className={`px-4 py-2.5 ${themeClasses.textSecondary}`}>{topic.question_count}</td>
                  <td className="px-4 py-2.5">
                    {topic.is_active
                      ? <span className="text-emerald-400 text-xs font-medium">{t('admin.credentialQuiz.active')}</span>
                      : <span className={`${themeClasses.textMuted} text-xs`}>{t('admin.credentialQuiz.hidden')}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right space-x-2">
                    <button onClick={() => openTopicModal(topic)} className={themeClasses.textSecondary} title={t('admin.credentialQuiz.editTopic')}>
                      <Edit2 size={15} />
                    </button>
                    <button onClick={() => removeTopic(topic)} className="text-red-400 hover:text-red-300" title={t('admin.credentialQuiz.deleteTopic')}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSelectedTopicId(null)}
              className={`flex items-center gap-1.5 text-sm ${themeClasses.textSecondary} hover:${themeClasses.text}`}
            >
              <ChevronLeft size={16} /> {t('admin.credentialQuiz.backToTopics')}
            </button>
            <button
              onClick={() => openQuestionModal(null)}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
            >
              <Plus size={16} /> {t('admin.credentialQuiz.newQuestion')}
            </button>
          </div>
          <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{selectedTopic?.name}</h3>

          {LEVELS.map((level) => (
            <div key={level}>
              <h4 className={`text-xs font-semibold uppercase tracking-wide ${themeClasses.textMuted} mb-2`}>{t(`credentialQuiz.levels.${level}`)}</h4>
              <div className="space-y-2">
                {questions.filter((q) => q.level === level).map((q) => (
                  <div key={q.id} className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${themeClasses.border} ${themeClasses.cardBg}`}>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${themeClasses.text}`}>{q.question_text}</p>
                      <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                        {t('admin.credentialQuiz.choicesCount', { count: q.choices.length, correct: q.correct_choice_key })}
                        {!q.is_active && t('admin.credentialQuiz.hiddenSuffix')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => openQuestionModal(q)} className={themeClasses.textSecondary}><Edit2 size={14} /></button>
                      <button onClick={() => removeQuestion(q)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
                {questions.filter((q) => q.level === level).length === 0 && (
                  <p className={`text-xs ${themeClasses.textMuted} italic`}>{t('admin.credentialQuiz.noQuestionsYet', { level: t(`credentialQuiz.levels.${level}`) })}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Topic modal */}
      {topicModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={(e) => e.target === e.currentTarget && setTopicModal(null)}>
          <div className={`w-full max-w-md rounded-2xl p-6 space-y-4 ${themeClasses.cardBg} border ${themeClasses.border}`}>
            <div className="flex items-center justify-between">
              <h3 className={`font-semibold ${themeClasses.text}`}>{topicModal === 'create' ? t('admin.credentialQuiz.topicModal.newTitle') : t('admin.credentialQuiz.topicModal.editTitle')}</h3>
              <button onClick={() => setTopicModal(null)}><X size={18} className={themeClasses.textMuted} /></button>
            </div>
            {topicModal === 'create' && (
              <div>
                <label className={`block text-xs mb-1 ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.topicModal.keyLabel')}</label>
                <input value={topicForm.key} onChange={(e) => setTopicForm({ ...topicForm, key: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${themeClasses.input}`} />
              </div>
            )}
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.topicModal.nameLabel')}</label>
              <input value={topicForm.name} onChange={(e) => setTopicForm({ ...topicForm, name: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${themeClasses.input}`} />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.topicModal.descriptionLabel')}</label>
              <textarea value={topicForm.description || ''} onChange={(e) => setTopicForm({ ...topicForm, description: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${themeClasses.input}`} rows={2} />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.topicModal.groupLabelLabel')}</label>
              <input value={topicForm.group_label || ''} onChange={(e) => setTopicForm({ ...topicForm, group_label: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${themeClasses.input}`} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={topicForm.is_active} onChange={(e) => setTopicForm({ ...topicForm, is_active: e.target.checked })} />
              <span className={themeClasses.textSecondary}>{t('admin.credentialQuiz.topicModal.activeCheckbox')}</span>
            </label>
            <button onClick={saveTopic} disabled={!topicForm.name || (topicModal === 'create' && !topicForm.key)}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
              {t('admin.credentialQuiz.topicModal.saveTopic')}
            </button>
          </div>
        </div>
      )}

      {/* Question modal */}
      {questionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={(e) => e.target === e.currentTarget && setQuestionModal(null)}>
          <div className={`w-full max-w-lg rounded-2xl p-6 space-y-4 my-8 ${themeClasses.cardBg} border ${themeClasses.border}`}>
            <div className="flex items-center justify-between">
              <h3 className={`font-semibold ${themeClasses.text}`}>{questionModal === 'create' ? t('admin.credentialQuiz.questionModal.newTitle') : t('admin.credentialQuiz.questionModal.editTitle')}</h3>
              <button onClick={() => setQuestionModal(null)}><X size={18} className={themeClasses.textMuted} /></button>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.questionModal.levelLabel')}</label>
              <select value={questionForm.level} onChange={(e) => setQuestionForm({ ...questionForm, level: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${themeClasses.input}`}>
                {LEVELS.map((l) => <option key={l} value={l}>{t(`credentialQuiz.levels.${l}`)}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.questionModal.questionTextLabel')}</label>
              <textarea value={questionForm.question_text} onChange={(e) => setQuestionForm({ ...questionForm, question_text: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${themeClasses.input}`} rows={2} />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.questionModal.choicesLabel')}</label>
              <div className="space-y-2">
                {questionForm.choices.map((c, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="radio" name="correct" checked={questionForm.correct_choice_key === c.key}
                      onChange={() => setQuestionForm({ ...questionForm, correct_choice_key: c.key })}
                    />
                    <input value={c.key} onChange={(e) => updateChoice(idx, 'key', e.target.value)}
                      className={`w-12 px-2 py-1.5 rounded-lg border text-sm ${themeClasses.input}`} maxLength={8} />
                    <input value={c.text} onChange={(e) => updateChoice(idx, 'text', e.target.value)}
                      placeholder={t('admin.credentialQuiz.questionModal.choiceTextPlaceholder')} className={`flex-1 px-2 py-1.5 rounded-lg border text-sm ${themeClasses.input}`} />
                    {questionForm.choices.length > 2 && (
                      <button onClick={() => removeChoice(idx)} className="text-red-400"><X size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={addChoice} className="mt-2 text-xs text-blue-400 hover:text-blue-300">{t('admin.credentialQuiz.questionModal.addChoice')}</button>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textMuted}`}>{t('admin.credentialQuiz.questionModal.explanationLabel')}</label>
              <textarea value={questionForm.explanation || ''} onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${themeClasses.input}`} rows={2} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={questionForm.is_active} onChange={(e) => setQuestionForm({ ...questionForm, is_active: e.target.checked })} />
              <span className={themeClasses.textSecondary}>{t('admin.credentialQuiz.questionModal.activeCheckbox')}</span>
            </label>
            <button
              onClick={saveQuestion}
              disabled={!questionForm.question_text || questionForm.choices.some((c) => !c.text) || !questionForm.correct_choice_key}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
            >
              <Check size={16} /> {t('admin.credentialQuiz.questionModal.saveQuestion')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCredentialQuizComponent;
